import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function ImageGallery({ images = [], alt = "Apartment image" }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    skipSnaps: false,
  });
  const [selected, setSelected] = useState(0);

  const scrollTo = useCallback(
    (i) => emblaApi && emblaApi.scrollTo(i),
    [emblaApi],
  );
  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi],
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  if (!images.length) return null;

  return (
    <div data-testid="apartment-image-gallery" className="w-full">
      <div className="relative overflow-hidden rounded-2xl shadow-[0_20px_50px_rgba(26,29,26,0.10)]">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {images.map((src, i) => (
              <div
                key={src + i}
                className="relative min-w-0 flex-[0_0_100%]"
              >
                <div className="aspect-[16/10] w-full bg-[#F7F4ED]">
                  <img
                    src={src}
                    alt={`${alt} ${i + 1}`}
                    loading={i === 0 ? "eager" : "lazy"}
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          data-testid="gallery-prev-button"
          onClick={scrollPrev}
          aria-label="Previous image"
          className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 text-[#1A1D1A] shadow-md backdrop-blur transition-all hover:bg-white md:left-6"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          type="button"
          data-testid="gallery-next-button"
          onClick={scrollNext}
          aria-label="Next image"
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 text-[#1A1D1A] shadow-md backdrop-blur transition-all hover:bg-white md:right-6"
        >
          <ChevronRight size={20} />
        </button>

        <div className="absolute bottom-4 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-white/85 px-3 py-1.5 text-[11px] tracking-wider text-[#4A524C] backdrop-blur-md">
          {selected + 1} / {images.length}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-5 gap-2.5 sm:grid-cols-7 md:grid-cols-9">
        {images.map((src, i) => (
          <button
            key={src + "-thumb-" + i}
            type="button"
            data-testid={`gallery-thumb-${i}`}
            onClick={() => scrollTo(i)}
            className={`aspect-square overflow-hidden rounded-xl border transition-all duration-300 ${
              selected === i
                ? "border-[#C6A66B] ring-2 ring-[#C6A66B]/40"
                : "border-[#D9CFC2] hover:border-[#C6A66B]"
            }`}
          >
            <img
              src={src}
              alt={`Thumbnail ${i + 1}`}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </button>
        ))}
      </div>
    </div>
  );
}
